import { useEffect, useState } from "react";
import { api } from "./api";
import { INITIAL_POLL, advancePoll, type PollState } from "./forecast-poll";
import type { Forecast } from "@shared/types";

export interface ForecastPollState extends PollState {
  data: Forecast | null;
  loading: boolean;
  error: string | null;
}

/** Single unified forecast poll: hits GET /forecast every `intervalMs`. 200 →
 * data + stop; 202 → advance the loader (monotonic pct via advancePoll) + keep
 * polling. Failures stop with an error. Resets on `electionId` change. */
export function useForecastPoll(electionId: string, intervalMs = 800): ForecastPollState {
  const [data, setData] = useState<Forecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [poll, setPoll] = useState<PollState>(INITIAL_POLL);

  useEffect(() => {
    if (!electionId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    let state = INITIAL_POLL;
    setData(null);
    setError(null);
    setLoading(true);
    setPoll(INITIAL_POLL);

    const tick = () =>
      api
        .forecastPoll(electionId)
        .then((r) => {
          if (!alive) return;
          if (r.ready) {
            setData(r.data);
            setLoading(false);
            return;
          }
          state = advancePoll(state, r.status);
          setPoll(state);
          timer = setTimeout(tick, intervalMs);
        })
        .catch((e) => {
          if (!alive) return;
          setError((e as Error).message);
          setLoading(false);
        });
    tick();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [electionId, intervalMs]);

  return { data, loading, error, pct: poll.pct, stage: poll.stage };
}
